import { DEFAULT_KEYPOINT_MODEL_URL } from './modelRegistry';
import { createPersistedConfigStore } from '../../config/persistedConfig';

export type StageSmoothingMode = 'raw' | 'advanced';
export type StageRoiMode = 'full' | 'seg' | 'track';
export type LiveFretboardDiagramPlacement =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'side';

export interface JamCamStageConfig {
  camera: {
    autoStart: boolean;
    autoFullscreen: boolean;
    preferredWidth: number;
    preferredHeight: number;
  };
  detection: {
    modelUrl: string;
    frameSkip: number;
    confidenceThreshold: number;
    roiMode: StageRoiMode;
    roiPad: number;
    roiSegInterval: number;
    trackPad: number;
    autoThreshold: {
      enabled: boolean;
      targetCoverage: number;
      minThreshold: number;
      maxThreshold: number;
    };
  };
  smoothing: {
    mode: StageSmoothingMode;
    holdoverEnabled: boolean;
    holdoverMs: number;
    minCutoff: number;
    beta: number;
    tunedPreset: boolean;
  };
  overlay: {
    showFps: boolean;
    /** Tuner requests the microphone, so it is opt-in (replaces the old `showTuner`). */
    tunerEnabled: boolean;
    showNoteStream: boolean;
    showLiveFretboardDiagram: boolean;
    liveFretboardDiagramPlacement: LiveFretboardDiagramPlacement;
    showLiveFretboardFretNumbers: boolean;
    showLiveFretboardScaleNotes: boolean;
    flipLiveFretboardHorizontal: boolean;
    flipLiveFretboardVertical: boolean;
    showKeypoints: boolean;
    showLabels: boolean;
    showConfidence: boolean;
    showDetectionGrid: boolean;
    pointRadius: number;
    keypointTopColor: string;
    keypointBottomColor: string;
    keypointHeldColor: string;
    stringGuideColor: string;
    fretPairColor: string;
  };
  fretboard: {
    homographySmooth: boolean;
    homographySmoothAlpha: number;
    perspectiveRoll: boolean;
    rollInsetTop: number;
    rollInsetBottom: number;
    nutNudgePx: number;
    fretRangeMin: number;
    fretRangeMax: number;
  };
}

export const defaultJamCamStageConfig: JamCamStageConfig = {
  camera: {
    autoStart: true,
    autoFullscreen: false,
    preferredWidth: 1920,
    preferredHeight: 1080,
  },
  detection: {
    modelUrl: DEFAULT_KEYPOINT_MODEL_URL,
    frameSkip: 0,
    confidenceThreshold: 0.92,
    roiMode: 'track' as StageRoiMode,
    roiPad: 0.03,
    roiSegInterval: 5,
    trackPad: 0.03,
    autoThreshold: {
      enabled: true,
      targetCoverage: 0.7,
      minThreshold: 0.8,
      maxThreshold: 0.96,
    },
  },
  smoothing: {
    mode: 'advanced',
    holdoverEnabled: false,
    holdoverMs: 2000,
    minCutoff: 0.18,
    beta: 0.19,
    tunedPreset: true,
  },
  overlay: {
    showFps: true,
    tunerEnabled: false,
    showNoteStream: false,
    showLiveFretboardDiagram: false,
    liveFretboardDiagramPlacement: 'top',
    showLiveFretboardFretNumbers: true,
    showLiveFretboardScaleNotes: true,
    flipLiveFretboardHorizontal: false,
    flipLiveFretboardVertical: false,
    showKeypoints: false,
    showLabels: true,
    showConfidence: true,
    showDetectionGrid: false,
    pointRadius: 5,
    keypointTopColor: 'rgba(143, 199, 255, 0.95)',
    keypointBottomColor: 'rgba(255, 123, 123, 0.95)',
    keypointHeldColor: 'rgba(255, 215, 90, 0.9)',
    stringGuideColor: 'rgba(143, 199, 255, 0.4)',
    fretPairColor: 'rgba(98, 220, 180, 0.82)',
  },
  fretboard: {
    homographySmooth: true,
    homographySmoothAlpha: 0.698,
    perspectiveRoll: true,
    rollInsetTop: -0.1,
    rollInsetBottom: 0.04,
    nutNudgePx: 4,
    fretRangeMin: 0,
    fretRangeMax: 18,
  },
};

// Bump this key whenever the default stage config changes in a way that should
// override any user-persisted config (e.g. switching the default keypoint model).
// v7 → v8: default model is now `web_dots_aws_v4_model` (AWS v4 edges).
export const stageConfigStore = createPersistedConfigStore(
  'jamcam-stage-config-v8',
  defaultJamCamStageConfig
);
